/**
 * TotpSetupModal — in-place 2FA enrollment (architect directive 2026-07-10;
 * POLICY_ACCOUNT_SECURITY_LADDER rule 2 JIT pattern).
 *
 * Hosts the existing TotpSetup flow in a standalone modal so every JIT
 * callsite (Action Center tray, legacy Tasks tab, VerificationPanel, and
 * future requires2fa 403 handlers) launches ONE enrollment experience in
 * place — no navigation, no Account Settings detour. TotpSetup invalidates
 * the security cache on confirm, so the enable_2fa task / gate state
 * resolves live, without a reload.
 *
 * Overlay is opaque per CLAUDE.md (bg-primary + bg-elevated composite).
 */

import React, { useEffect } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useAuth } from '../../auth/AuthContext';
import TotpSetup from './TotpSetup';
import styles from './TotpSetupModal.module.css';

export interface TotpSetupModalProps {
  open: boolean;
  onClose: () => void;
}

const TotpSetupModal: React.FC<TotpSetupModalProps> = ({ open, onClose }) => {
  const { t } = useAppTranslation('pages');
  const { user } = useAuth();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={styles.backdrop} role="presentation">
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="totp-setup-modal-title"
      >
        <div className={styles.header}>
          <h2 id="totp-setup-modal-title" className={styles.title}>
            {t('totpModal.title')}
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t('totpModal.close')}
          >
            ×
          </button>
        </div>
        <p className={styles.hint}>{t('totpModal.hint')}</p>
        <div className={styles.body}>
          <TotpSetup isSystemAdmin={!!user?.isSystemAdmin} />
        </div>
      </div>
    </div>
  );
};

export default TotpSetupModal;
